// helpNudge — when to offer "Stuck? Talk to support" (support plan, 26 Oct).
//
// Every failed save goes through the mutation-cache subscriber in main.tsx,
// which calls noteFailedSave(). Two failed saves on the same page within five
// minutes is the sign someone is stuck, so the bubble is offered. It is never
// offered again within ten minutes of the last offer, and "Not now" puts it
// away for thirty. Offline it stays quiet: saves queue, and a call cannot start.
//
// Nothing about the error itself is kept here: only the page and the time.

export const NUDGE_EVENT = 'bigagri:help-nudge'

const WINDOW_MS = 5 * 60_000
const COOLDOWN_MS = 10 * 60_000
const SNOOZE_MS = 30 * 60_000
const FAILS_NEEDED = 2

export interface NudgeState {
  fails: ReadonlyArray<{ path: string; at: number }>
  lastShown: number
  snoozedUntil: number
}

export const EMPTY_NUDGE: NudgeState = { fails: [], lastShown: -Infinity, snoozedUntil: -Infinity }

/** Record one failed save on `path`; say whether to offer help now. Pure. */
export function nextNudge(state: NudgeState, path: string, now: number): { state: NudgeState; show: boolean } {
  const fails = [...state.fails.filter(f => now - f.at < WINDOW_MS), { path, at: now }]
  const here = fails.filter(f => f.path === path).length
  const quiet = now < state.snoozedUntil || now - state.lastShown < COOLDOWN_MS
  if (here < FAILS_NEEDED || quiet) return { state: { ...state, fails }, show: false }
  // Offered: start counting afresh.
  return { state: { ...state, fails: [], lastShown: now }, show: true }
}

/** "Not now": quiet for half an hour. Pure. */
export function snoozed(state: NudgeState, now: number): NudgeState {
  return { ...state, snoozedUntil: now + SNOOZE_MS }
}

let current: NudgeState = EMPTY_NUDGE

/** Called for every failed save (main.tsx). Fires NUDGE_EVENT when it is time. */
export function noteFailedSave(path: string = window.location.pathname, now: number = Date.now()): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  // A save that failed while the app was in the background is not someone stuck.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
  const r = nextNudge(current, path, now)
  current = r.state
  if (r.show) window.dispatchEvent(new CustomEvent(NUDGE_EVENT))
}

export function snoozeNudge(now: number = Date.now()): void {
  current = snoozed(current, now)
}
